import { useEffect, useState } from "react";

interface AuthProviders {
  google: boolean;
  apple: boolean;
  email: boolean;
}

const DEFAULT_PROVIDERS: AuthProviders = {
  google: false,
  apple: false,
  email: true,
};

/**
 * Reads /auth/v1/settings to know which auth providers are actually
 * enabled in the Supabase project. Used to hide OAuth buttons that
 * would otherwise return "Unsupported provider: provider is not enabled"
 * when clicked. Self-healing: when a provider is enabled in the
 * dashboard, the button reappears automatically without a code change.
 *
 * Provenance: discovered via auth_logs from the 2026-05-05 pilot —
 * 8+ /authorize 400s in 90 seconds because the prominent "Continue
 * with Google" button on /signup pointed at an unconfigured provider.
 */
export function useAuthProviders(): AuthProviders {
  const [providers, setProviders] = useState<AuthProviders>(DEFAULT_PROVIDERS);

  useEffect(() => {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
    const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
    if (!supabaseUrl || !anonKey) return;

    fetch(`${supabaseUrl}/auth/v1/settings`, {
      headers: { apikey: anonKey, Authorization: `Bearer ${anonKey}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data?.external) return;
        setProviders({
          google: !!data.external.google,
          apple: !!data.external.apple,
          email: !!data.external.email,
        });
      })
      .catch((err) => {
        console.error("[useAuthProviders] settings fetch failed:", err);
      });
  }, []);

  return providers;
}
